import { TemplateFragmentModel } from '@/typings/api/emailCenter';

export type FragmentDrawerMode = 'create' | 'edit' | 'clone';

/**
 * The visual editor's component library: product-managed rows shared by every
 * partner. They are not listed and not editable — the API assigns every new
 * key itself, so there is no way to write one either.
 */
export const RESERVED_FRAGMENT_PREFIX = 'core/';

/** Whether the fragment catalog offers any editor at all for this key. */
export function isAuthorableFragmentKey(key: string): boolean {
  return !key.startsWith(RESERVED_FRAGMENT_PREFIX);
}

/**
 * The mode a fragment editor may actually run in.
 *
 * A fragment the partner does not own is never edited in place: the row is
 * product-managed (or another partner's) and `TemplateFragmentPolicy::update`
 * refuses the write. What is offered instead is a copy — an independent block
 * with its own key, which leaves the standard one and every email including it
 * exactly as they were. So an `edit` on a non-owned record is a clone, decided
 * here rather than at each call site, so no entry point can open an editor
 * whose Save could only ever 403.
 *
 * A requested `clone` is always a clone: copying a block one owns is a valid
 * create, not an edit of the original.
 */
export function resolveFragmentDrawerMode(
  requestedMode: FragmentDrawerMode,
  record: TemplateFragmentModel | null,
): FragmentDrawerMode {
  if (requestedMode !== 'edit' || !record) return requestedMode;
  return record.is_owned ? 'edit' : 'clone';
}

/**
 * Whether the **template** editor may open this fragment's details at all.
 *
 * Only blocks the partner owns. Editing a standard one from a template resolved
 * to a clone ({@link resolveFragmentDrawerMode}) and every copy gets a key of
 * its own — so the save produced a block *this email does not include* while the
 * canvas kept rendering the standard row: an edit sitting in the database under
 * a key nothing references, with nothing on screen to explain it.
 *
 * The Theme tab still offers that copy, where it is the point of the action and
 * the result is listed beside the block it came from.
 */
export function isFragmentOpenableFromTemplate(
  fragment: Pick<TemplateFragmentModel, 'key' | 'is_owned'> | null,
): boolean {
  if (!fragment) return false;
  return isAuthorableFragmentKey(fragment.key) && fragment.is_owned;
}
